import { FuelType, VehicleViewModel } from '@ail/contracts-api'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { printDate } from '../../../helpers/Utils'
import LicencePlateFlag from '../../common/LicencePlateFlag/LicencePlateFlag'

const InstallationIcons: Record<string, string | undefined> = {
  '0001': 'icon-inEmptyTop',
  '0010': 'icon-inRight',
  '0011': 'icon-inRightTop',
  '0100': 'icon-inCenter',
  '0101': 'icon-inCenterTop',
  '0110': 'icon-inRightCenter',
  '0111': 'icon-inRightCenterTop',
  '1000': 'icon-inLeft',
  '1001': 'icon-inLeftTop',
  '1010': 'icon-inLeftRight',
  '1011': 'icon-inLeftRightTop',
  '1100': 'icon-inLeftCenter',
  '1101': 'icon-inLeftCenterTop',
  '1110': 'icon-inAll',
  '1111': 'icon-inAllTop',
}

export function VehicleDetails(props: { vehicle?: VehicleViewModel; onCopy: (value?: string) => void }) {
  const { t } = useTranslation('common')

  const fuelIcon = useMemo(() => {
    switch (props.vehicle?.fuelType) {
      case FuelType.Electric:
        return 'icon-electric'
      case FuelType.HybridDiesel:
      case FuelType.HybridPetrol:
        return 'icon-hybrid'
      default:
        return 'icon-fuel'
    }
  }, [props.vehicle])

  const installations = useMemo(() => {
    if (!props.vehicle) {
      return undefined
    }
    const { InteriorLeft, InteriorCenter, InteriorRight, ExteriorTop, ExteriorBack } = props.vehicle

    const left = typeof InteriorLeft === 'string' && InteriorLeft !== 'None' ? InteriorLeft : ''
    const center = typeof InteriorCenter === 'string' && InteriorCenter !== 'None' ? InteriorCenter : ''
    const right = typeof InteriorRight === 'string' && InteriorRight !== 'None' ? InteriorRight : ''
    const top = typeof ExteriorTop === 'string' && ExteriorTop !== 'None' ? ExteriorTop : ''
    const back = typeof ExteriorBack === 'string' && ExteriorBack !== 'None' ? ExteriorBack : ''

    const mask = `${+!!left}${+!!center}${+!!right}${+!!top}`

    return { left, center, right, top, back, mask, icon: InstallationIcons[mask] }
  }, [props.vehicle])

  return (
    <div className="detailContentArea carDetail">
      <div className="detailTitle">
        <div>
          <i className="icon-car" /> <span>{t('CAR_DETAILS')}</span>
        </div>
        {props.vehicle?.licensePlate?.licenseNumber && (
          <LicencePlateFlag
            licenceNumber={props.vehicle?.licensePlate?.licenseNumber}
            countryCode={props.vehicle?.licensePlate?.country}
            width="15"
            height="17"
          />
        )}
      </div>
      <div className="details">
        <div className="row">
          <div className="col-6">
            <div className="detailContent">
              {props.vehicle?.makeName &&
              props.vehicle?.makeName.toLowerCase() !== 'dummy' &&
              props.vehicle?.modelName &&
              props.vehicle?.modelName.toLowerCase() !== 'dummy' ? (
                <label>{(props.vehicle?.makeName + ' ' + props.vehicle?.modelName).toUpperCase()}</label>
              ) : (
                <label>{props.vehicle?.description}</label>
              )}
            </div>
            {props.vehicle?.typeName && (
              <div className="detailContent">
                <label>{t('MODEL')}</label>
                {props.vehicle?.typeName.toLowerCase() === 'dummy'
                  ? props.vehicle?.description
                  : props.vehicle?.typeName}
              </div>
            )}
          </div>
          <div className="col-6">
            {props.vehicle?.makeName && (
              <img
                src={`../../../../../makesLogos/${
                  props.vehicle?.makeName.toLowerCase().indexOf('ladestation') > -1
                    ? 'dummy'
                    : props.vehicle?.makeName
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .replace(/\s+/g, '-')
                        .toLowerCase()
                }.svg`}
                width="100%"
                height="100%"
              />
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div
              style={props.vehicle?.chassisNumber ? { cursor: 'pointer' } : undefined}
              className="detailContent"
              onClick={() => props.onCopy(props.vehicle?.chassisNumber)}
            >
              <label style={props.vehicle?.chassisNumber ? { cursor: 'pointer' } : undefined}>{t('VIN_NUMBER')}</label>
              {(props.vehicle?.chassisNumber && props.vehicle?.chassisNumber) || '-'}
            </div>
          </div>
          <div className="col-6">
            {!!props.vehicle?.motorNumber && (
              <div
                style={props.vehicle?.motorNumber ? { cursor: 'pointer' } : undefined}
                onClick={() => props.onCopy(props.vehicle?.motorNumber?.match(/.{3}/g)?.join('.'))}
                className="detailContent"
              >
                <label style={props.vehicle?.motorNumber ? { cursor: 'pointer' } : undefined}>
                  {t('MOTOR_NUMBER')}
                </label>
                {props.vehicle?.motorNumber?.match(/.{3}/g)?.join('.')}
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="detailContent">
              <label>{t('FIRST_CIRCOLATION_DATE')}</label>
              {printDate(props.vehicle?.registrationDate)}
            </div>
          </div>
          <div className="col-6">
            <div className="detailContent">
              <label>{t('TYRE_SPECIFICATION')}</label>
              {props.vehicle?.tiresSpecification ? props.vehicle.tiresSpecification : ' - '}
            </div>
          </div>
        </div>
        <div className="detailsBottomArea">
          <div className="detBottom">
            <i className={fuelIcon} />
            <span>{props.vehicle?.fuelType ? props.vehicle.fuelType : t('NOT_SPECIFIED')}</span>
          </div>
          <div className="detBottom">
            <i className="icon-engine" />
            <span>{props.vehicle?.powerKw ? props.vehicle.powerKw : ' - '} kW</span>
          </div>
          <div className="detBottom">
            <i className="icon-co2" />
            <span>{props.vehicle?.co2Emission ? props.vehicle.co2Emission : ' - '}g/Km</span>
          </div>
          <div className="detBottom noBorR">
            <i className="icon-consfuel" />
            <span>
              {props.vehicle?.consumption
                ? `${props.vehicle.consumption} ${props.vehicle.consumptionUnit ?? 'l/100km'}`
                : 'N/A'}
            </span>
          </div>

          {!!installations && installations.mask !== '0000' && (
            <div className="detBottom intInfo">
              <span className="tooltipText">
                {!!installations.left && <>{`Left: ${installations.left}`}</>}
                {!!installations.right && (
                  <>
                    <br />
                    {`Right: ${installations.right}`}
                  </>
                )}
                {!!installations.center && (
                  <>
                    <br />
                    {`Center: ${installations.center}`}
                  </>
                )}
                {!!installations.top && (
                  <>
                    <br />
                    {`Top: ${installations.top}`}
                  </>
                )}
                {!!installations.back && (
                  <>
                    <br />
                    {`Back: ${installations.back}`}
                  </>
                )}
              </span>
              <i className={installations.icon} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
